import React from "react";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import cls from "./styles.module.scss";

const getErrorMessage = (error: unknown) => {
  if (isRouteErrorResponse(error)) return `${error.status} ${error.statusText}`;
  if (error instanceof Error) return error.message;
  return "Unknown error";
};

const getErrorStackTrace = (error: unknown, componentStack = "") => {
  let errorStack = String(error);
  if (error instanceof Error) errorStack = error.stack ?? "";
  else if (isRouteErrorResponse(error)) errorStack = String(error.data ?? "");

  return [
    getErrorMessage(error),
    errorStack,
    componentStack ? `Component stack:${componentStack}` : "",
  ]
    .filter(Boolean)
    .join("\n\n");
};

interface ErrorFallbackProps {
  error: unknown;
  componentStack?: string;
}

export const ErrorFallback = ({
  error,
  componentStack = "",
}: ErrorFallbackProps) => {
  const [copied, setCopied] = React.useState(false);
  const stackTrace = getErrorStackTrace(error, componentStack);

  const copyStackTrace = async () => {
    try {
      await navigator.clipboard.writeText(stackTrace);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <main className={cls.root}>
      <section className={cls.panel}>
        <p className={cls.eyebrow}>Что-то пошло не так</p>
        <h1 className={cls.title}>Не удалось открыть экран</h1>
        <p className={cls.message}>
          Попробуйте обновить страницу. Если ошибка повторится, отправьте детали
          в поддержку.
        </p>
        <button type="button" className={cls.copyButton} onClick={copyStackTrace}>
          {copied ? "Скопировано" : "Скопировать детали"}
        </button>
        <pre className={cls.stackTrace}>
          <code>{stackTrace}</code>
        </pre>
      </section>
    </main>
  );
};

export const RouteErrorBoundary = () => {
  const error = useRouteError();
  return <ErrorFallback error={error} />;
};

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  error: unknown;
  componentStack: string;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null, componentStack: "" };
  }

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? "" });
  }

  render() {
    const { error, componentStack } = this.state;
    const { children } = this.props;
    if (error) {
      return <ErrorFallback error={error} componentStack={componentStack} />;
    }
    return children;
  }
}
